import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";

const MAX_CACHED_DOCUMENTS = 4;

const MAX_CANVAS_PIXELS = 16_000_000;

const documents = new Map();

/** One download per PDF URL, shared by the page card and the zoom modal. */
export function loadPdfDocument(url) {
  if (pdfjsLib.GlobalWorkerOptions.workerSrc !== pdfjsWorker) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
  }
  if (!documents.has(url)) {
    if (documents.size >= MAX_CACHED_DOCUMENTS) {
      const [oldestUrl, oldest] = documents.entries().next().value;
      documents.delete(oldestUrl);
      oldest.then((pdf) => pdf.destroy()).catch(() => {});
    }
    const promise = pdfjsLib.getDocument({
      url,
      withCredentials: false,
    }).promise;
    promise.catch(() => documents.delete(url));
    documents.set(url, promise);
  }
  return documents.get(url);
}

/** Device pixels per CSS pixel, capped so huge zooms stay renderable. */
export function renderScaleFor(page, cssScale) {
  const base = page.getViewport({ scale: 1 });
  const ratio = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  const wanted = cssScale * ratio;
  const maxScale = Math.sqrt(MAX_CANVAS_PIXELS / (base.width * base.height));
  return Math.min(wanted, maxScale);
}

/**
 * Renders a page into a fresh offscreen canvas, so whatever is on screen
 * stays visible until the new frame is complete.
 * @returns {{ promise: Promise<HTMLCanvasElement>, cancel: () => void }}
 */
export function renderPageOffscreen(page, scale) {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const task = page.render({
    canvasContext: canvas.getContext("2d"),
    viewport,
  });
  return {
    promise: task.promise.then(() => canvas),
    cancel: () => task.cancel(),
  };
}

/** First page as a sharp image sized for a card about `cssWidth` px wide. */
export async function renderPdfThumbnail(url, cssWidth = 360) {
  const pdf = await loadPdfDocument(url);
  const page = await pdf.getPage(1);
  const base = page.getViewport({ scale: 1 });
  const scale = renderScaleFor(page, (cssWidth / base.width) * 2);
  const canvas = await renderPageOffscreen(page, scale).promise;
  return {
    dataUrl: canvas.toDataURL("image/png"),
    width: base.width,
    height: base.height,
  };
}
